import { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { RotateCw } from "lucide-react";
import { useLocale } from "@/context/LocaleContext";

/* -----------------------------------------------------------------------------
 * Cart3D — Realistic PBR rendering of the NOVIFOOD cart.
 *
 * Improvements vs. previous version:
 *  - Physically-based materials (clearcoat, glass transmission, chrome).
 *  - Real-time shadows (PCFSoftShadowMap) + ACESFilmic tone mapping.
 *  - Room environment (PMREM) for realistic reflections without HDRI.
 *  - Rounded chassis panels (via BoxGeometry + edge chamfer illusion).
 *  - Wheels: rubber tire + metallic hub with 5 spokes.
 *  - Glass display case on the counter (transparent, refraction).
 *  - Awning valance with scalloped edge (matches physical prop of retractable
 *    fabric roof). Solar panel textured with a subtle grid.
 *  - 3-point lighting (key + fill + rim) with soft shadows.
 * ---------------------------------------------------------------------------*/

const DEG = Math.PI / 180;

/* Chamfered box — approximates a bevel by using a slightly larger box behind
 * the visible one to fake soft edges. Good enough for realtime. */
function chamferedBox(w, h, d, color, opts = {}) {
    const geo = new THREE.BoxGeometry(w, h, d, 2, 2, 2);
    const mat = new THREE.MeshPhysicalMaterial({
        color,
        roughness: 0.45,
        metalness: 0.0,
        clearcoat: 0.6,
        clearcoatRoughness: 0.2,
        ...opts,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
}

function chromeMat(opts = {}) {
    return new THREE.MeshPhysicalMaterial({
        color: 0xdde3ea,
        roughness: 0.15,
        metalness: 1.0,
        clearcoat: 1.0,
        clearcoatRoughness: 0.05,
        ...opts,
    });
}

function glassMat(color = 0xffffff) {
    return new THREE.MeshPhysicalMaterial({
        color,
        roughness: 0.05,
        metalness: 0.0,
        transmission: 0.95,
        thickness: 0.15,
        ior: 1.5,
        clearcoat: 1.0,
        clearcoatRoughness: 0.05,
        opacity: 1,
        transparent: true,
        side: THREE.DoubleSide,
    });
}

function paintMat(hex, opts = {}) {
    return new THREE.MeshPhysicalMaterial({
        color: hex,
        roughness: 0.35,
        metalness: 0.05,
        clearcoat: 0.85,
        clearcoatRoughness: 0.15,
        ...opts,
    });
}

function fabricMat(hex, opts = {}) {
    // Awning fabric — matte, slight fuzz
    return new THREE.MeshPhysicalMaterial({
        color: hex,
        roughness: 0.9,
        metalness: 0.0,
        sheen: 0.6,
        sheenColor: new THREE.Color(hex).multiplyScalar(1.2),
        clearcoat: 0.0,
        side: THREE.DoubleSide,
        ...opts,
    });
}

function woodMat() {
    // Warm walnut counter — procedural (no texture file dependency)
    return new THREE.MeshPhysicalMaterial({
        color: 0x8b5a2b,
        roughness: 0.55,
        metalness: 0.0,
        clearcoat: 0.6,
        clearcoatRoughness: 0.25,
    });
}

/* Simple canvas texture used for the solar panel grid + awning stripes */
function makeStripesTexture(colorA, colorB, stripes = 6) {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 128;
    const ctx = canvas.getContext("2d");
    const w = canvas.width / stripes;
    for (let i = 0; i < stripes; i++) {
        ctx.fillStyle = i % 2 === 0 ? colorA : colorB;
        ctx.fillRect(i * w, 0, w, canvas.height);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    return tex;
}

function makeSolarTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0F2547"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1E3A6B"; ctx.lineWidth = 3;
    const cols = 8, rows = 4;
    const cw = canvas.width / cols, rh = canvas.height / rows;
    for (let i = 1; i < cols; i++) { ctx.beginPath(); ctx.moveTo(i * cw, 0); ctx.lineTo(i * cw, canvas.height); ctx.stroke(); }
    for (let j = 1; j < rows; j++) { ctx.beginPath(); ctx.moveTo(0, j * rh); ctx.lineTo(canvas.width, j * rh); ctx.stroke(); }
    // subtle diagonal highlights inside cells
    ctx.fillStyle = "rgba(60,100,180,0.25)";
    for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
            ctx.beginPath();
            ctx.moveTo(i * cw + 4, j * rh + 4);
            ctx.lineTo((i + 1) * cw - 4, j * rh + 4);
            ctx.lineTo((i + 1) * cw - 4, (j + 1) * rh - 4);
            ctx.closePath();
            ctx.fill();
        }
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/* Scalloped valance geometry (drape hanging from awning) */
function scallopedValance(width, depthDrop, scallops = 12) {
    const shape = new THREE.Shape();
    const step = width / scallops;
    shape.moveTo(-width / 2, 0);
    shape.lineTo(width / 2, 0);
    // Bottom edge with half-circle scallops
    for (let i = scallops; i >= 1; i--) {
        const x0 = -width / 2 + i * step;
        const x1 = x0 - step;
        // half circle arc from x0 down and back up to x1
        shape.quadraticCurveTo((x0 + x1) / 2, -depthDrop * 1.6, x1, 0);
    }
    shape.lineTo(-width / 2, 0);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.015, bevelEnabled: false });
    return geo;
}

function buildWheel(radius = 0.19) {
    const g = new THREE.Group();
    // Tire (torus)
    const tire = new THREE.Mesh(
        new THREE.TorusGeometry(radius, 0.055, 20, 40),
        new THREE.MeshPhysicalMaterial({ color: 0x181c22, roughness: 0.9, metalness: 0.1, clearcoat: 0.1 }),
    );
    tire.castShadow = true;
    g.add(tire);
    // Rim
    const rim = new THREE.Mesh(
        new THREE.CylinderGeometry(radius - 0.05, radius - 0.05, 0.045, 24),
        chromeMat({ color: 0xcfd5dc }),
    );
    rim.rotation.x = Math.PI / 2;
    rim.castShadow = true;
    g.add(rim);
    // Spokes (5 rectangular)
    for (let i = 0; i < 5; i++) {
        const spoke = new THREE.Mesh(
            new THREE.BoxGeometry(0.028, radius * 1.4, 0.025),
            chromeMat({ color: 0xb8bfc7 }),
        );
        spoke.rotation.z = (i / 5) * Math.PI * 2;
        spoke.castShadow = true;
        g.add(spoke);
    }
    // Hub cap
    const hub = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.05, 0.05, 20),
        chromeMat({ color: 0xf3f5f8 }),
    );
    hub.rotation.x = Math.PI / 2;
    hub.castShadow = true;
    g.add(hub);
    return g;
}

function buildCart(scene, props) {
    const { canopyType, colors, hasSolar, hasLighting, hasFreezer } = props;
    const length = props.sizeId === "1.35m" ? 1.4 : 2.0;
    const group = new THREE.Group();
    const primary = new THREE.Color(colors?.primary || "#1E3A5F");
    const accent = new THREE.Color(colors?.accent || "#F59E0B");
    const cream = new THREE.Color("#F4EFE4");
    const L = length;

    // ---------- Chassis ----------
    // Lower kick (dark navy metallic)
    const kick = new THREE.Mesh(
        new THREE.BoxGeometry(L + 0.02, 0.14, 0.94),
        paintMat(0x0B2545, { metalness: 0.4, roughness: 0.25 }),
    );
    kick.position.set(0, 0.24, 0);
    kick.castShadow = true; kick.receiveShadow = true;
    group.add(kick);

    // Main painted panel (primary colour)
    const frontPanel = new THREE.Mesh(
        new THREE.BoxGeometry(L, 0.55, 0.92),
        paintMat(primary),
    );
    frontPanel.position.set(0, 0.55, 0);
    frontPanel.castShadow = true; frontPanel.receiveShadow = true;
    group.add(frontPanel);

    // Cream side rails (thin bands)
    [-0.29, 0.29].forEach((y) => {
        const rail = new THREE.Mesh(
            new THREE.BoxGeometry(L + 0.02, 0.03, 0.94),
            paintMat(cream, { clearcoat: 0.9 }),
        );
        rail.position.set(0, 0.55 + y, 0);
        group.add(rail);
    });

    // Wood counter top
    const counter = new THREE.Mesh(
        new THREE.BoxGeometry(L + 0.1, 0.06, 1.0),
        woodMat(),
    );
    counter.position.set(0, 0.87, 0);
    counter.castShadow = true; counter.receiveShadow = true;
    group.add(counter);

    // ---------- Front details ----------
    // Brand plate (accent color)
    const brand = new THREE.Mesh(
        new THREE.BoxGeometry(L * 0.5, 0.2, 0.02),
        paintMat(accent, { metalness: 0.5, roughness: 0.35 }),
    );
    brand.position.set(0, 0.58, 0.47);
    group.add(brand);

    // Chrome handle bar
    const handle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.018, 0.018, L * 0.7, 12),
        chromeMat(),
    );
    handle.rotation.z = Math.PI / 2;
    handle.position.set(0, 0.36, 0.47);
    handle.castShadow = true;
    group.add(handle);

    // Hinges (2 chrome discs)
    [-L * 0.28, L * 0.28].forEach((x) => {
        const hinge = new THREE.Mesh(
            new THREE.CylinderGeometry(0.02, 0.02, 0.03, 16),
            chromeMat(),
        );
        hinge.rotation.x = Math.PI / 2;
        hinge.position.set(x, 0.36, 0.475);
        group.add(hinge);
    });

    // ---------- Glass display on counter ----------
    if (hasFreezer) {
        // Refrigerated glass display case (rear side of counter)
        const dispW = L * 0.55;
        const dispH = 0.34;
        const dispD = 0.42;
        const glassBox = new THREE.Mesh(
            new THREE.BoxGeometry(dispW, dispH, dispD),
            glassMat(0xeaf2f6),
        );
        glassBox.position.set(-L * 0.18, 0.9 + dispH / 2, -0.1);
        glassBox.castShadow = true;
        group.add(glassBox);
        // Chrome frame around glass (edges as thin cylinders on each corner)
        const frameMat = chromeMat({ color: 0xdde3ea });
        const half = { w: dispW / 2, h: dispH / 2, d: dispD / 2 };
        const corners = [
            [-half.w, -half.h, -half.d], [half.w, -half.h, -half.d],
            [-half.w, half.h, -half.d], [half.w, half.h, -half.d],
            [-half.w, -half.h, half.d], [half.w, -half.h, half.d],
            [-half.w, half.h, half.d], [half.w, half.h, half.d],
        ];
        corners.forEach(([cx, cy, cz]) => {
            const post = new THREE.Mesh(
                new THREE.SphereGeometry(0.015, 8, 8),
                frameMat,
            );
            post.position.set(-L * 0.18 + cx, 0.9 + dispH / 2 + cy, -0.1 + cz);
            group.add(post);
        });
    }

    // ---------- Wheels ----------
    [-L / 2 + 0.24, L / 2 - 0.24].forEach((x) => {
        const w = buildWheel(0.19);
        w.position.set(x, 0.19, 0);
        w.rotation.y = 0.02;
        group.add(w);
    });

    // ---------- Canopy posts ----------
    const hasCanopy = canopyType && canopyType !== "none";
    if (hasCanopy && canopyType !== "umbrella") {
        [-L / 2 + 0.12, L / 2 - 0.12].forEach((x) => {
            const post = new THREE.Mesh(
                new THREE.CylinderGeometry(0.025, 0.025, 1.1, 14),
                chromeMat(),
            );
            post.position.set(x, 1.45, -0.36);
            post.castShadow = true;
            group.add(post);
            // Rear cross-brace
            const crossbrace = new THREE.Mesh(
                new THREE.CylinderGeometry(0.015, 0.015, 0.15, 10),
                chromeMat(),
            );
            crossbrace.rotation.z = 0.4;
            crossbrace.position.set(x + 0.05, 1.9, -0.35);
            group.add(crossbrace);
        });
    }

    // ---------- Canopy ----------
    if (canopyType === "umbrella") {
        const pole = new THREE.Mesh(
            new THREE.CylinderGeometry(0.02, 0.02, 1.6, 14),
            chromeMat(),
        );
        pole.position.set(0, 1.7, 0);
        pole.castShadow = true;
        group.add(pole);
        const umbrella = new THREE.Mesh(
            new THREE.ConeGeometry(1.05, 0.5, 24, 1, false),
            fabricMat(primary),
        );
        umbrella.position.set(0, 2.4, 0);
        umbrella.castShadow = true;
        group.add(umbrella);
    } else if (canopyType === "flat" || canopyType === "awning") {
        // Roof panel
        const tilt = canopyType === "awning" ? -0.28 : 0;
        const roofW = L + 0.4;
        const roofD = canopyType === "awning" ? 1.35 : 1.2;
        const roof = new THREE.Mesh(
            new THREE.BoxGeometry(roofW, 0.06, roofD),
            paintMat(cream, { clearcoat: 0.7 }),
        );
        roof.position.set(0, 2.05, canopyType === "awning" ? 0.08 : -0.05);
        roof.rotation.x = tilt;
        roof.castShadow = true; roof.receiveShadow = true;
        group.add(roof);

        // Striped underside (canvas texture) — visible from below
        const stripeTex = makeStripesTexture("#F4EFE4", primary.getStyle(), 10);
        const underside = new THREE.Mesh(
            new THREE.BoxGeometry(roofW - 0.02, 0.005, roofD - 0.02),
            new THREE.MeshBasicMaterial({ map: stripeTex, side: THREE.DoubleSide }),
        );
        underside.position.set(0, 2.02, canopyType === "awning" ? 0.08 : -0.05);
        underside.rotation.x = tilt;
        group.add(underside);

        // Scalloped valance drape at the front edge
        const valanceGeo = scallopedValance(roofW, 0.12, 14);
        const valance = new THREE.Mesh(valanceGeo, fabricMat(accent));
        // orient hanging vertically at the front edge of the roof
        valance.position.set(0, 2.05, canopyType === "awning" ? 0.66 : 0.53);
        valance.rotation.x = tilt - Math.PI / 2;
        valance.castShadow = true;
        group.add(valance);
    }

    // ---------- Solar panel ----------
    if (hasSolar && (canopyType === "awning" || canopyType === "flat")) {
        const solarTex = makeSolarTexture();
        const solar = new THREE.Mesh(
            new THREE.BoxGeometry(L * 0.7, 0.02, 0.55),
            new THREE.MeshPhysicalMaterial({
                map: solarTex,
                roughness: 0.28,
                metalness: 0.6,
                clearcoat: 0.9,
                clearcoatRoughness: 0.1,
            }),
        );
        solar.position.set(0, 2.11, canopyType === "awning" ? 0.02 : -0.1);
        solar.rotation.x = canopyType === "awning" ? -0.28 : 0;
        solar.castShadow = true;
        group.add(solar);
    }

    // ---------- LED lighting strip ----------
    if (hasLighting) {
        const led = new THREE.Mesh(
            new THREE.BoxGeometry(L * 0.9, 0.025, 0.02),
            new THREE.MeshPhysicalMaterial({
                color: 0xFFF3D0,
                emissive: 0xFFD75E,
                emissiveIntensity: 2.2,
                roughness: 0.4,
                metalness: 0.0,
            }),
        );
        led.position.set(0, 0.95, 0.48);
        group.add(led);
        // Soft glow from LED as a small point light
        const glow = new THREE.PointLight(0xffdd88, 0.6, 1.6, 2);
        glow.position.set(0, 0.95, 0.5);
        group.add(glow);
    }

    // ---------- Ground ----------
    const ground = new THREE.Mesh(
        new THREE.CircleGeometry(3.2, 64),
        new THREE.MeshPhysicalMaterial({
            color: 0xd8d3c2,
            roughness: 0.85,
            metalness: 0.0,
            clearcoat: 0.1,
        }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.005;
    ground.receiveShadow = true;
    group.add(ground);

    // subtle cart tilt
    group.rotation.x = 0.02;
    scene.add(group);
    return group;
}

/* -------------------------------------------------------------------------- */

export default function Cart3D(props) {
    const { tr } = useLocale();
    const mountRef = useRef(null);
    const spinRef = useRef(0);
    const propsRef = useRef(props);
    propsRef.current = props;

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const scene = new THREE.Scene();
        scene.background = null;

        const camera = new THREE.PerspectiveCamera(38, 4 / 3, 0.1, 50);
        camera.position.set(3.3, 2.0, 3.4);
        camera.lookAt(0, 0.85, 0);

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.05;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        mount.appendChild(renderer.domElement);

        // Environment map for realistic reflections (no external HDRI needed)
        const pmrem = new THREE.PMREMGenerator(renderer);
        pmrem.compileEquirectangularShader();
        const envTex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        scene.environment = envTex;

        // --- 3-point lighting ---
        scene.add(new THREE.AmbientLight(0xffffff, 0.35));

        const key = new THREE.DirectionalLight(0xfff5e1, 2.0);
        key.position.set(4, 6, 3);
        key.castShadow = true;
        key.shadow.mapSize.set(1024, 1024);
        key.shadow.camera.left = -3;
        key.shadow.camera.right = 3;
        key.shadow.camera.top = 3;
        key.shadow.camera.bottom = -3;
        key.shadow.camera.near = 0.5;
        key.shadow.camera.far = 20;
        key.shadow.bias = -0.0006;
        key.shadow.normalBias = 0.02;
        scene.add(key);

        const fill = new THREE.DirectionalLight(0xdae7f8, 0.55);
        fill.position.set(-4, 3, -2);
        scene.add(fill);

        const rim = new THREE.DirectionalLight(0xffffff, 0.8);
        rim.position.set(0, 2, -4);
        scene.add(rim);

        let group = buildCart(scene, propsRef.current);
        let lastProps = JSON.stringify({ ...propsRef.current });
        let rotY = 0.6;
        let dragging = false;
        let lastX = 0;

        const resize = () => {
            const w = mount.clientWidth;
            const h = mount.clientHeight;
            renderer.setSize(w, h);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(mount);

        const onDown = (e) => { dragging = true; lastX = e.clientX ?? e.touches?.[0]?.clientX ?? 0; };
        const onUp = () => { dragging = false; };
        const onMove = (e) => {
            if (!dragging) return;
            const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
            rotY += (x - lastX) * 0.012;
            lastX = x;
        };
        const el = renderer.domElement;
        el.style.cursor = "grab";
        el.addEventListener("pointerdown", onDown);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointermove", onMove);

        let raf;
        const clock = new THREE.Clock();
        const tick = () => {
            const dt = clock.getDelta();
            if (spinRef.current > 0) {
                const speed = Math.max(0.6, spinRef.current) * 1.9;
                const step = Math.min(spinRef.current, dt * speed);
                rotY += step;
                spinRef.current -= step;
                if (spinRef.current < 0.002) spinRef.current = 0;
            } else if (!dragging) {
                rotY += dt * 0.32;
            }
            const cur = JSON.stringify({ ...propsRef.current });
            if (cur !== lastProps) {
                lastProps = cur;
                scene.remove(group);
                group.traverse((o) => {
                    o.geometry?.dispose?.();
                    if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
                    else o.material?.dispose?.();
                });
                group = buildCart(scene, propsRef.current);
            }
            group.rotation.y = rotY;
            renderer.render(scene, camera);
            raf = requestAnimationFrame(tick);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            el.removeEventListener("pointerdown", onDown);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointermove", onMove);
            pmrem.dispose();
            renderer.dispose();
            try { mount.removeChild(el); } catch (e) { /* noop */ }
        };
    }, []);

    return (
        <div
            className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-border shadow-inner"
            style={{
                background: "radial-gradient(ellipse at 50% 30%, #F6F3EC 0%, #DCE3EB 60%, #A8B5C6 100%)",
            }}
            data-testid="cart-3d"
        >
            <div ref={mountRef} className="absolute inset-0" />
            {/* soft vignette overlay for depth */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: "radial-gradient(ellipse at 50% 100%, transparent 55%, rgba(11,37,69,0.16) 100%)",
                }}
            />
            <button
                type="button"
                data-testid="cart-360-btn"
                onClick={() => { spinRef.current = Math.PI * 2 * ({}.constructor === Object ? 1 : 1); }}
                title={tr("Ver rotação 360º", "Watch 360º rotation")}
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-primary/90 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-white backdrop-blur hover:bg-primary transition"
            >
                <RotateCw className="h-3.5 w-3.5" /> 360º
            </button>
            <span className="absolute bottom-3 left-3 rounded-full bg-white/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary backdrop-blur pointer-events-none">
                {tr("Arraste para rodar · PBR realista", "Drag to rotate · realistic PBR")}
            </span>
        </div>
    );
}
